import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createUserWithEmailAndPassword, deleteUser, getAuth, signOut } from "firebase/auth";
import { captureSignupLead } from "../lib/signupLeadApi";
import { completeBusinessSignup } from "../lib/businessSignupApi";
import { createTenantSubscriptionCheckout } from "../lib/tenantBillingApi";
import TurnstileWidget from "../components/TurnstileWidget";

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f6f7fb",
    padding: "48px 24px",
  },
  wrap: {
    maxWidth: 1180,
    margin: "0 auto",
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 28,
  },
  logo: {
    width: 250,
    maxWidth: "100%",
    height: "auto",
  },
  backBtn: {
    appearance: "none",
    border: "1px solid #d7dce8",
    background: "#fff",
    color: "#2a0516",
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  hero: {
    marginBottom: 28,
  },
  title: {
    margin: 0,
    color: "#2a0516",
    fontSize: "clamp(24px, 5vw, 38px)",
    lineHeight: 1.12,
    fontWeight: 800,
  },
  subtitle: {
    margin: "12px 0 0",
    color: "#5f6b7a",
    fontSize: 17,
    lineHeight: 1.6,
    maxWidth: 760,
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 360px) 1fr",
    gap: 24,
    alignItems: "start",
  },
  card: {
    background: "#fff",
    border: "1px solid #e8ebf3",
    borderRadius: 18,
    padding: 24,
    boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
  },
  smallLabel: {
    margin: 0,
    color: "#657899",
    fontSize: 13,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  planName: {
    margin: "10px 0 0",
    color: "#2a0516",
    fontSize: 28,
    fontWeight: 800,
  },
  priceRow: {
    marginTop: 12,
    display: "flex",
    alignItems: "baseline",
    gap: 8,
  },
  price: {
    color: "#111827",
    fontSize: 40,
    fontWeight: 800,
  },
  cadence: {
    color: "#657899",
    fontSize: 16,
    fontWeight: 600,
  },
  description: {
    marginTop: 12,
    color: "#5f6b7a",
    fontSize: 15,
    lineHeight: 1.6,
    minHeight: 70,
  },
  featureList: {
    listStyle: "none",
    padding: 0,
    margin: "18px 0 0",
    display: "grid",
    gap: 10,
  },
  featureItem: {
    color: "#1f2937",
    fontSize: 15,
    lineHeight: 1.5,
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
  },
  tick: {
    color: "#166534",
    fontWeight: 800,
    marginTop: 1,
  },
  formTitle: {
    margin: 0,
    color: "#2a0516",
    fontSize: 28,
    fontWeight: 800,
  },
  formText: {
    margin: "10px 0 0",
    color: "#5f6b7a",
    fontSize: 15,
    lineHeight: 1.6,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginTop: 22,
  },
  fieldWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  fullWidth: {
    gridColumn: "1 / -1",
  },
  label: {
    color: "#657899",
    fontSize: 14,
    fontWeight: 700,
  },
  input: {
    width: "100%",
    border: "1px solid #d7dce8",
    background: "#fff",
    borderRadius: 12,
    padding: "14px 16px",
    fontSize: 15,
    color: "#111827",
    outline: "none",
    boxSizing: "border-box",
  },
  helper: {
    color: "#6b7280",
    fontSize: 13,
    lineHeight: 1.5,
  },
  infoBox: {
    marginTop: 18,
    border: "1px solid #e8ebf3",
    background: "#fafbff",
    borderRadius: 12,
    padding: "14px 16px",
    color: "#4b5563",
    fontSize: 14,
    lineHeight: 1.6,
  },
  successBox: {
    marginTop: 18,
    border: "1px solid #d7eadf",
    background: "#f3fbf6",
    color: "#166534",
    borderRadius: 12,
    padding: "12px 14px",
    fontSize: 14,
    lineHeight: 1.5,
  },
  errorBox: {
    marginTop: 18,
    border: "1px solid #f3c7c4",
    background: "#fff5f4",
    color: "#b42318",
    borderRadius: 12,
    padding: "12px 14px",
    fontSize: 14,
    lineHeight: 1.5,
  },
  checkboxRow: {
    marginTop: 18,
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  },
  checkbox: {
    marginTop: 3,
    width: 18,
    height: 18,
    accentColor: "#2a0516",
    cursor: "pointer",
  },
  checkboxLabel: {
    color: "#4b5563",
    fontSize: 14,
    lineHeight: 1.6,
  },
  link: {
    color: "#2a0516",
    fontWeight: 700,
    textDecoration: "underline",
  },
  actions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 22,
  },
  primaryBtn: {
    appearance: "none",
    border: "1px solid #2a0516",
    background: "#2a0516",
    color: "#fff",
    borderRadius: 10,
    padding: "12px 18px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(42,5,22,0.12)",
  },
  secondaryBtn: {
    appearance: "none",
    border: "1px solid #d7dce8",
    background: "#fff",
    color: "#2a0516",
    borderRadius: 10,
    padding: "12px 18px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  disabledBtn: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

const PLAN_META = {
  starter: {
    name: "Starter",
    price: "€199.99",
    cadence: "/ month",
    description:
      "A strong entry point for smaller organisations that need secure access, role-based controls, and room to get started properly.",
    features: [
      "Up to 25 seats",
      "Owner and HR admin controls",
      "Tenant member management",
      "3 account managers per member",
      "5 GB secure storage per member",
      "Stripe billing and billing portal",
      "Upgrade path into Growth",
    ],
  },
  growth: {
    name: "Growth",
    price: "€399.99",
    cadence: "/ month",
    description:
      "For larger teams that need more capacity, stronger rollout support, and a cleaner scaling path as internal usage grows.",
    features: [
      "Up to 50 seats",
      "Everything in Starter",
      "Higher seat allowance",
      "3 account managers per member",
      "5 GB secure storage per member",
      "Immediate upgrade path from Starter",
      "Best fit for growing business use",
    ],
  },
  family: {
    name: "Family Plan",
    price: "€199.99",
    cadence: "/ year",
    description:
      "One plan for the whole family. Each member gets their own private account with full digital vault access.",
    features: [
      "Up to 5 family members",
      "Each member gets their own private account",
      "3 account managers per member",
      "5 GB secure storage per member",
      "1 family organiser manages invites",
      "Priority support",
    ],
  },
};

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leadTimerRef = useRef(null);

  const planFromQuery = String(searchParams.get("plan") || "starter").toLowerCase();
  const selectedPlan = PLAN_META[planFromQuery] ? planFromQuery : "starter";
  const planMeta = PLAN_META[selectedPlan];
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

  const [form, setForm] = useState({
    companyName: "",
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    agreedToTerms: false,
  });

  const [error, setError] = useState("");
  const [leadMessage, setLeadMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [leadId, setLeadId] = useState(() => {
    try {
      return sessionStorage.getItem("lwb_business_signup_lead_id") || "";
    } catch (e) {
      return "";
    }
  });

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("lwb_business_signup_draft");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setForm((prev) => ({
        ...prev,
        companyName: parsed.companyName || "",
        fullName: parsed.fullName || "",
        email: parsed.email || "",
        agreedToTerms: !!parsed.agreedToTerms,
      }));
    } catch (e) {}
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        "lwb_business_signup_draft",
        JSON.stringify({
          companyName: form.companyName,
          fullName: form.fullName,
          email: form.email,
          planKey: selectedPlan,
          agreedToTerms: form.agreedToTerms,
        })
      );
    } catch (e) {}
  }, [form.companyName, form.fullName, form.email, form.agreedToTerms, selectedPlan]);

  useEffect(() => {
    const companyName = form.companyName.trim();
    const email = form.email.trim().toLowerCase();

    if (!companyName || !isValidEmail(email)) {
      return;
    }

    if (leadTimerRef.current) {
      clearTimeout(leadTimerRef.current);
    }

    leadTimerRef.current = setTimeout(async () => {
      try {
        const result = await captureSignupLead({
          leadId: leadId || "",
          email,
          companyName,
          planKey: selectedPlan,
          source: "website",
          status: "started",
          page: window.location.pathname + window.location.search,
          notes: "",
        });

        if (result?.leadId) {
          setLeadId(result.leadId);
          sessionStorage.setItem("lwb_business_signup_lead_id", result.leadId);
          setLeadMessage("Progress saved.");
        }
      } catch (e) {
        console.error("Lead capture failed", e);
      }
    }, 800);

    return () => {
      if (leadTimerRef.current) {
        clearTimeout(leadTimerRef.current);
      }
    };
  }, [form.companyName, form.email, selectedPlan, leadId]);

  const formComplete = useMemo(() => {
    return (
      form.companyName.trim() &&
      form.fullName.trim() &&
      form.email.trim() &&
      form.password &&
      form.confirmPassword
    );
  }, [form]);

  function updateField(name, value) {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  async function ensureLeadExists() {
    const companyName = form.companyName.trim();
    const email = form.email.trim().toLowerCase();

    if (leadId) return leadId;

    const result = await captureSignupLead({
      leadId: "",
      email,
      companyName,
      planKey: selectedPlan,
      source: "website",
      status: "started",
      page: window.location.pathname + window.location.search,
      notes: "",
    });

    if (result?.leadId) {
      setLeadId(result.leadId);
      sessionStorage.setItem("lwb_business_signup_lead_id", result.leadId);
      return result.leadId;
    }

    return "";
  }

  async function handleContinue(e) {
    e.preventDefault();
    setError("");

    if (!formComplete) {
      setError("Please complete all fields before continuing.");
      return;
    }

    if (!isValidEmail(form.email)) {
      setError("Please enter a valid work email address.");
      return;
    }

    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!form.agreedToTerms) {
      setError("Please confirm that you have read and agree to the Terms & Conditions.");
      return;
    }

    if (!turnstileToken) {
      setError("Please complete the security check.");
      return;
    }

    let createdAuthUser = null;
    let tenantCreated = false;

    try {
      setSubmitting(true);

      const ensuredLeadId = await ensureLeadExists();
      const auth = getAuth();

      window.__lwb_signup_in_progress = true;
      const authResult = await createUserWithEmailAndPassword(
        auth,
        form.email.trim().toLowerCase(),
        form.password
      );

      createdAuthUser = authResult.user;

      const result = await completeBusinessSignup({
        companyName: form.companyName.trim(),
        fullName: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        planKey: selectedPlan,
        leadId: ensuredLeadId,
        agreedToTerms: true,
        turnstileToken,
      });

      if (!result?.tenantId) {
        throw new Error("Tenant creation did not return a tenantId");
      }

      tenantCreated = true;

      sessionStorage.setItem("lwb_business_signup_tenant_id", result.tenantId);
      sessionStorage.setItem("lwb_business_signup_plan_key", selectedPlan);
      sessionStorage.setItem("lwb_business_signup_checkout_pending", "1");

      const checkout = await createTenantSubscriptionCheckout(result.tenantId, selectedPlan);

      if (!checkout?.url) {
        throw new Error("Account created, but Stripe checkout could not be started.");
      }

      try {
        sessionStorage.removeItem("lwb_business_signup_draft");
        sessionStorage.removeItem("lwb_business_signup_lead_id");
      } catch (e) {}

      window.location.replace(checkout.url);
      return;
    } catch (err) {
      console.error(err);

      try {
        if (createdAuthUser && !tenantCreated) {
          const auth = getAuth();
          if (auth.currentUser && auth.currentUser.uid === createdAuthUser.uid) {
            await deleteUser(auth.currentUser);
          } else {
            await signOut(auth);
          }
        }
      } catch (rollbackErr) {
        console.error("Signup rollback failed", rollbackErr);
        try {
          await signOut(getAuth());
        } catch (signOutErr) {}
      }

      if (err?.code === "auth/email-already-in-use") {
        // Try to recover — sign them in and resume checkout if pending
        try {
          const { signInWithEmailAndPassword, getAuth } = await import("firebase/auth");
          const { doc, getDoc } = await import("firebase/firestore");
          const { db: fireDb } = await import("../firebase");
          const auth = getAuth();
          const cred = await signInWithEmailAndPassword(auth, form.email.trim().toLowerCase(), form.password);
          const uid = cred.user.uid;
          const userSnap = await getDoc(doc(fireDb, "users", uid));
          const userData = userSnap.exists() ? userSnap.data() : {};
          const tenantId = userData.primaryTenantId || userData.tenantId || "";
          if (tenantId) {
            const tenantSnap = await getDoc(doc(fireDb, "tenants", tenantId));
            const tenantData = tenantSnap.exists() ? tenantSnap.data() : {};
            if (tenantData.billingStatus === "pending_checkout") {
              const checkout = await createTenantSubscriptionCheckout(tenantId, selectedPlan);
              if (checkout?.url) {
                window.location.replace(checkout.url);
                return;
              }
            }
          }
          setError("You already have an account. Please log in to manage your subscription.");
        } catch {
          setError("An account with this email already exists. Please log in or use a different email.");
        }
      } else if (err?.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else if (err?.code === "auth/weak-password") {
        setError("Password must be stronger. Please use at least 8 characters.");
      } else if (tenantCreated) {
        setError(err.message || "Your account was created, but payment could not be started. Please sign in and complete payment.");
      } else {
        setError(err.message || "Unable to create your account right now.");
      }
      setSubmitting(false);
    }
  }

  if (submitting) {
    return (
      <div style={{ minHeight: "100vh", background: "#f6f7fb", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", background: "#fff", borderRadius: 16, padding: "40px 32px", boxShadow: "0 10px 40px rgba(42,5,22,0.10)", maxWidth: 440, width: "100%" }}>
          <img src="/Lastwishbox-logo.png" alt="LastWish Box" style={{ height: 48, marginBottom: 24 }} />
          <h2 style={{ margin: "0 0 8px", color: "#650202", fontSize: 22, fontWeight: 800 }}>Setting up your account</h2>
          <p style={{ margin: "0 0 24px", color: "#657899", fontSize: 15 }}>Please wait while we prepare your secure checkout…</p>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", border: "3px solid #f0e8ec", borderTopColor: "#980000", animation: "lwb-spin 0.8s linear infinite" }} />
          </div>
          <style>{"@keyframes lwb-spin { to { transform: rotate(360deg); } }"}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>{`
        .lwb-signup-layout { display: grid; grid-template-columns: minmax(0, 360px) 1fr; gap: 24px; align-items: start; }
        @media (max-width: 768px) {
          .lwb-signup-layout { grid-template-columns: 1fr !important; }
          .lwb-signup-layout > *:first-child { order: 2; }
          .lwb-signup-layout > *:last-child { order: 1; }
        }
      `}</style>
      <div style={styles.wrap}>
        <div style={styles.topRow}>
          <img src="/Lastwishbox-logo.png" alt="LastWishBox" style={styles.logo} />
          <button type="button" onClick={() => navigate("/plans")} style={styles.backBtn}>
            ← Back to plans
          </button>
        </div>

        <div style={styles.hero}>
          <h1 style={styles.title}>Create your LastWishBox Business account</h1>
          <p style={styles.subtitle}>
            Set up your organisation, choose your plan, and create the owner account for your
            tenant workspace.
          </p>
        </div>

        <div className="lwb-signup-layout">
          <div style={styles.card}>
            <p style={styles.smallLabel}>Selected plan</p>
            <h2 style={styles.planName}>{planMeta.name}</h2>

            <div style={styles.priceRow}>
              <span style={styles.price}>{planMeta.price}</span>
              <span style={styles.cadence}>{planMeta.cadence}</span>
            </div>

            <div style={styles.description}>{planMeta.description}</div>

            <ul style={styles.featureList}>
              {planMeta.features.map((feature) => (
                <li key={feature} style={styles.featureItem}>
                  <span style={styles.tick}>✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div style={styles.card}>
            <h2 style={styles.formTitle}>Business signup</h2>
            <p style={styles.formText}>
              Enter your organisation and owner account details below. Once a valid company name and
              email are entered, the signup lead is captured automatically.
            </p>

            <form onSubmit={handleContinue}>
              <div style={styles.formGrid}>
                <div style={styles.fieldWrap}>
                  <label style={styles.label}>Company name</label>
                  <input
                    type="text"
                    value={form.companyName}
                    onChange={(e) => updateField("companyName", e.target.value)}
                    style={styles.input}
                    placeholder="Acme Ltd"
                  />
                </div>

                <div style={styles.fieldWrap}>
                  <label style={styles.label}>Owner full name</label>
                  <input
                    type="text"
                    value={form.fullName}
                    onChange={(e) => updateField("fullName", e.target.value)}
                    style={styles.input}
                    placeholder="Jane Murphy"
                  />
                </div>

                <div style={{ ...styles.fieldWrap, ...styles.fullWidth }}>
                  <label style={styles.label}>Work email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    style={styles.input}
                    placeholder="jane@company.ie"
                  />
                  <div style={styles.helper}>
                    This should be the main owner/admin email for the tenant account.
                  </div>
                </div>

                <div style={styles.fieldWrap}>
                  <label style={styles.label}>Password</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => updateField("password", e.target.value)}
                    style={styles.input}
                    placeholder="Minimum 8 characters"
                  />
                </div>

                <div style={styles.fieldWrap}>
                  <label style={styles.label}>Confirm password</label>
                  <input
                    type="password"
                    value={form.confirmPassword}
                    onChange={(e) => updateField("confirmPassword", e.target.value)}
                    style={styles.input}
                    placeholder="Re-enter password"
                  />
                </div>
              </div>

              <div style={styles.checkboxRow}>
                <input
                  id="agreedToTerms"
                  type="checkbox"
                  checked={form.agreedToTerms}
                  onChange={(e) => updateField("agreedToTerms", e.target.checked)}
                  style={styles.checkbox}
                />
                <label htmlFor="agreedToTerms" style={styles.checkboxLabel}>
                  I have read and agree to the{" "}
                  <a
                    href="https://business.lastwishbox.com/policies/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.link}
                  >
                    Terms &amp; Conditions
                  </a>
                  {" "}and{" "}
                  <a
                    href="https://business.lastwishbox.com/policies/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.link}
                  >
                    Privacy Policy
                  </a>.
                </label>
              </div>

              {turnstileSiteKey ? (
                <div style={{ marginTop: 18 }}>
                  <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    onVerify={(token) => setTurnstileToken(token)}
                    onExpire={() => setTurnstileToken("")}
                    onError={() => setTurnstileToken("")}
                  />
                </div>
              ) : null}

              <div style={styles.infoBox}>
                This step now creates the owner account and provisions the tenant record in
                Firestore. The next step will hand the new customer into Stripe automatically.
              </div>

              {leadMessage ? <div style={styles.successBox}>{leadMessage}</div> : null}
              {error ? <div style={styles.errorBox}>{error}</div> : null}

              <div style={styles.actions}>
                <button
                  type="submit"
                  style={{
                    ...styles.primaryBtn,
                    ...(submitting ? styles.disabledBtn : {}),
                  }}
                  disabled={submitting}
                >
                  {submitting ? "Creating account..." : "Continue"}
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/plans")}
                  style={styles.secondaryBtn}
                  disabled={submitting}
                >
                  Change plan
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
